"use client";

import { useEffect, useMemo } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Comment, PostComment } from "@theta/api-client";
import { getAllComments } from "@/lib/api/posts";
import {
  applyPendingLocal,
  forgetThread,
  markThreadComplete,
  mergeSeed,
  seedThread,
} from "@/lib/thread";
import { livePollOptions } from "./useLivePolling";
import { useIfChanged } from "./useIfChanged";
import { retryUnlessRefused } from "./queryRetry";

/** One frozen reference, so an impossible `unchanged`-before-first-read is a
 *  stable empty thread rather than a new array every tick. */
const EMPTY: Comment[] = [];

/**
 * A post's whole comment thread — `GET /posts/{id}/comments?depth=all`.
 *
 * DEPTH-FIRST AND SELF-DESCRIBING: each row carries `depth` and `parentId`, so
 * the thread renders by indenting rows in the order they arrive. Nothing here
 * rebuilds a tree, and nothing compares a `reply_to_post_number` against a
 * `post_number` — v1 has neither (§1.2/C4).
 *
 * WHOLE, VIA THE CURSOR — NOT VIA A BIG `limit`. This asked for `limit: 200`,
 * which every paged route rejects (`min(1).max(100)` inside a
 * `z.strictObject`), so the request was a 400 `badRequest` EVERY time: the
 * thread rendered empty under a card saying "14 kommentarer", and because a
 * `badRequest` is not a `notFound` the poll below kept re-issuing it once a
 * second for as long as the tab was open. `getAllComments` reads pages at the
 * contract's own maximum and follows `nextCursor`.
 *
 * ---------------------------------------------------------------------------
 * ONE ENTRY PER THREAD, WHICHEVER PAGE THE CARD IS DRAWN ON — OSO-563
 * ---------------------------------------------------------------------------
 * This used to be the post page's hook alone. A feed card rendered its own
 * copy of the same rows out of the feed payload and kept THREE pieces of state
 * in sync by hand, which is why a reply written in the feed could sit
 * invisible until a reload: the write landed in this cache, and the feed card
 * was not reading it.
 *
 * So a feed card reads this too, and hands over its embedded preview as
 * `seed`. Two consequences worth stating:
 *
 *  - A SEEDED THREAD DOES NOT FETCH. `live` is false for a feed card, so the
 *    query is disabled: the rows come from the seed, and from the write-through
 *    in `useCreateComment`. Twenty cards on a page cost zero extra requests.
 *  - THE SEED IS REPUBLISHED, NOT JUST INITIAL. The feed keeps polling at the
 *    server's own cadence, so its snapshot has to keep reaching this entry or
 *    a card nobody is interacting with would freeze at first paint.
 *    `mergeSeed` decides what a snapshot is allowed to undo — and the seed is
 *    BOUNDED (`FEED_COMMENT_LIMIT`), so once this hook has read a thread whole
 *    the preview may refresh its rows but never shorten it.
 */
export function useComments(
  postId: number,
  options: { seed?: PostComment[]; live?: boolean } = {},
) {
  const { seed, live = true } = options;
  const queryClient = useQueryClient();
  const memory = useIfChanged<Comment[]>(postId);

  // The feed hands back the SAME page object while nothing has changed
  // (`useIfChanged` and the three infinite feeds all guarantee it), so this memo
  // holds across a poll that moved nothing and the effect below stays quiet.
  const seeded = useMemo(
    () => (seed ? seedThread(seed) : undefined),
    [seed],
  );

  useEffect(() => forgetThreadsOnEviction(queryClient), [queryClient]);

  useEffect(() => {
    if (!seeded) return;
    queryClient.setQueryData<Comment[]>(commentsKey(postId), (old) =>
      mergeSeed(old, seeded, postId),
    );
  }, [seeded, postId, queryClient]);

  return useQuery({
    queryKey: commentsKey(postId),
    queryFn: async () => {
      const fetched = memory.resolve(
        await getAllComments(postId, { depth: "all", ifChanged: memory.token() }),
        EMPTY,
      );
      /**
       * THE WHOLE THREAD, not the feed's last ten — so from here on the feed's
       * snapshot may refresh these rows but not shorten the list.
       *
       * `getAllComments` stops after `MAX_THREAD_PAGES` and says nothing about
       * having stopped, so a thread past 1,000 comments would be marked
       * complete on a truncated read. Left as is deliberately: the largest
       * thread in this corpus is 14 comments, the walk's own header explains
       * why the ceiling is where it is, and the failure it would cause — a
       * deleted comment lingering on an idle card — is the same one the mark
       * already tolerates.
       */
      markThreadComplete(postId);
      // A poll can land between a write's `onMutate` and its `onSuccess`, and
      // this is the document that would overwrite the placeholder.
      return applyPendingLocal(
        fetched,
        queryClient.getQueryData<Comment[]>(commentsKey(postId)),
        postId,
      );
    },
    // A seeded card is not waiting for anything; a live thread is.
    enabled: postId > 0 && live,
    ...(seeded ? { initialData: seeded } : {}),
    retry: retryUnlessRefused,
    /**
     * 1 Hz while the tab is visible, and `ifChanged` is what makes that
     * affordable: an unchanged tick costs the server one fingerprint query
     * over the thread's ids and answers with no document. Before OSO-489 this
     * walked the whole thread, hydrated every row and re-sent it, once a
     * second, whether or not anybody had written anything.
     */
    ...(live ? livePollOptions() : IDLE),
  });
}

/**
 * A thread nobody is in. It is not stale — it is fed by the feed's own poll
 * and by every local write — so there is nothing for a mount or a window focus
 * to go and get.
 */
const IDLE = {
  refetchInterval: false as const,
  refetchOnMount: false as const,
  refetchOnWindowFocus: false as const,
  staleTime: Infinity,
  gcTime: 5 * 60 * 1000,
} as const;

export function commentsKey(postId: number): (string | number)[] {
  return ["v1", "comments", postId];
}

/**
 * WHAT `lib/thread.ts` REMEMBERS LIVES EXACTLY AS LONG AS THE CACHE ENTRY.
 *
 * There is no other honest moment to forget it. The ledger cannot notice on
 * its own — `mergeSeed` only runs for a seeded card, and a seeded card always
 * has data, because react-query writes `initialData` into the entry while the
 * query is being constructed, before any effect runs. So the eviction itself
 * is the signal, and the cache is the thing that knows.
 *
 * One subscription per `QueryClient`, not one per mounted card: twenty feed
 * cards are twenty callers of this hook and one listener between them.
 */
const wired = new WeakSet<QueryClient>();

function forgetThreadsOnEviction(queryClient: QueryClient): void {
  if (wired.has(queryClient)) return;
  wired.add(queryClient);
  queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "removed") return;
    const [namespace, kind, postId] = event.query.queryKey;
    if (namespace === "v1" && kind === "comments" && typeof postId === "number") {
      forgetThread(postId);
    }
  });
}
